import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Label } from "@/components/ui/label";
import { LayoutGrid } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import * as SelectPrimitive from "@radix-ui/react-select";
import { ColorVariant } from "@/lib/colors";

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface StandardSelectProps {
  id: string;
  label: string;
  icon?: React.ReactNode;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  options: SelectOption[];
  placeholder?: string;
  required?: boolean;
  className?: string;
  isMulti?: boolean;
  colorVariant?: ColorVariant;
  iconColor?: string;
}

export function StandardSelect({
  id,
  label,
  icon,
  value,
  onChange,
  options,
  placeholder = "Select an option",
  required = false,
  className,
  isMulti = false,
  colorVariant = "blue",
  iconColor,
}: StandardSelectProps) {
  // For multi-select, we need to handle arrays
  const handleChange = (selectedValue: string) => {
    if (isMulti) {
      const currentValues = Array.isArray(value) ? value : [];
      
      // If value is already selected, remove it; otherwise add it
      if (currentValues.includes(selectedValue)) {
        onChange(currentValues.filter(v => v !== selectedValue));
      } else {
        onChange([...currentValues, selectedValue]);
      }
    } else {
      // For single select, convert "none" back to empty string
      onChange(selectedValue === "none" ? "" : selectedValue);
    }
  };

  // Determine what to display in the trigger
  const displayValue = () => {
    if (isMulti && Array.isArray(value)) {
      if (value.length === 0) return placeholder;
      if (value.length === 1) {
        const option = options.find(opt => opt.value === value[0]);
        return option ? option.label : placeholder;
      }
      return `${value.length} selected`;
    }
    
    // For single select
    if (!value || value === '') return placeholder;
    
    // When 'none' is selected, show the placeholder instead
    if (value === 'none') return placeholder;
    
    const option = options.find(opt => opt.value === value);
    return option ? option.label : placeholder;
  };

  // Color variant mappings
  const colorVariantMap = {
    blue: {
      border: "border-blue-200 dark:border-blue-800 focus:border-blue-400 focus-visible:border-blue-400 hover:border-blue-300",
      bg: "bg-blue-50/30 dark:bg-blue-900/20",
      iconDefault: "text-blue-500 dark:text-blue-400"
    },
    green: {
      border: "border-green-200 dark:border-green-800 focus:border-green-400 focus-visible:border-green-400 hover:border-green-300",
      bg: "bg-green-50/30 dark:bg-green-900/20",
      iconDefault: "text-green-500 dark:text-green-400"
    },
    purple: {
      border: "border-purple-200 dark:border-purple-800 focus:border-purple-400 focus-visible:border-purple-400 hover:border-purple-300",
      bg: "bg-purple-50/30 dark:bg-purple-900/20",
      iconDefault: "text-purple-500 dark:text-purple-400"
    },
    amber: {
      border: "border-amber-200 dark:border-amber-800 focus:border-amber-400 focus-visible:border-amber-400 hover:border-amber-300",
      bg: "bg-amber-50/30 dark:bg-amber-900/20",
      iconDefault: "text-amber-500 dark:text-amber-400"
    },
    orange: {
      border: "border-orange-200 dark:border-orange-800 focus:border-orange-400 focus-visible:border-orange-400 hover:border-orange-300",
      bg: "bg-orange-50/30 dark:bg-orange-900/20",
      iconDefault: "text-orange-500 dark:text-orange-400"
    },
    red: {
      border: "border-red-200 dark:border-red-800 focus:border-red-400 focus-visible:border-red-400 hover:border-red-300",
      bg: "bg-red-50/30 dark:bg-red-900/20",
      iconDefault: "text-red-500 dark:text-red-400"
    },
    indigo: {
      border: "border-indigo-200 dark:border-indigo-800 focus:border-indigo-400 focus-visible:border-indigo-400 hover:border-indigo-300",
      bg: "bg-indigo-50/30 dark:bg-indigo-900/20",
      iconDefault: "text-indigo-500 dark:text-indigo-400"
    }
  };

  const colorClasses = colorVariantMap[colorVariant] || colorVariantMap.blue;
  const iconColorClass = iconColor || colorClasses.iconDefault;

  // Convert current value for select (empty string becomes "none")
  const selectValue = Array.isArray(value) ? (value[0] || "none") : (value || "none");

  // Custom SelectTrigger with matching chevron color
  const CustomSelectTrigger = React.forwardRef<
    React.ElementRef<typeof SelectPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
  >(({ className, children, ...props }, ref) => (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "w-full bg-white/30 dark:bg-white/10 backdrop-blur-sm",
        colorClasses.border,
        colorClasses.bg,
        "transition-all duration-300 h-10 px-4 py-2 rounded-md text-sm font-medium",
        "text-foreground",
        "shadow-sm hover:shadow-md flex items-center gap-2",
        "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
        (!value || value === '') && "text-muted-foreground",
        className
      )}
      {...props}
    >
      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
        {icon ? React.cloneElement(icon as React.ReactElement, {
          className: iconColorClass
        }) : <LayoutGrid size={16} className={iconColorClass} />}
      </div>
      <div className="flex-grow text-left">
        {children}
      </div>
      <SelectPrimitive.Icon asChild>
        <ChevronDown className={cn("h-4 w-4", iconColorClass)} />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  ));

  return (
    <div className="space-y-2">
      <Select
        value={selectValue}
        onValueChange={handleChange}
      >
        <CustomSelectTrigger id={id}>
          <SelectValue placeholder={placeholder}>{displayValue()}</SelectValue>
        </CustomSelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem 
              key={option.value || "none"} 
              value={option.value || "none"}
              className={cn(
                "flex items-center text-foreground",
                isMulti && Array.isArray(value) && value.includes(option.value) && "bg-accent/20"
              )}
            >
              {option.label}
              {isMulti && Array.isArray(value) && value.includes(option.value) && (
                <span className="ml-auto">✓</span>
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
